<template>
  <div v-if="document">
    <a
      href="#"
      @click.prevent="openDocumentPopup"
      class="training-section-document-item"
    >
      <div class="document-card">
        <svg
          ref="shape"
          class="document-shape"
          xmlns="http://www.w3.org/2000/svg"
          width="195"
          height="283"
          viewBox="0 0 195 283"
          fill="none"
        >
          <path
            d="M144 1.5V51.5H194"
            stroke="var(--corp_color, #2A5388)"
            stroke-width="3"
          />
          <path
            d="M143.5 1.5L193 51V281.5H1.5V1.5H143.5Z"
            stroke="var(--corp_color, #2A5388)"
            stroke-width="3"
          />
        </svg>

        <div class="document-content">
          <div
            v-if="title"
            class="document-text"
            v-html="title"
          ></div>
        </div>
        <div
          v-if="title"
          ref="overlay"
          class="document-title-overlay"
          :style="overlayStyle"
          v-html="title"
        ></div>
      </div>
    </a>

    <teleport to="#popups">
      <transition name="fade" mode="out-in">
        <Popup
          v-if="popupActive"
          :isShown="popupActive"
          @modal-closed="closeDocumentPopup"
          :class="'fullscreen'"
        >
          <DocumentViewerPopup
            :document="document"
            :title="title"
          />
        </Popup>
      </transition>
    </teleport>
  </div>
</template>

<script>
import Popup from '@/components/Popup.vue';
import DocumentViewerPopup from '@/components/DocumentViewerPopup.vue';
import { unfreeze } from '@/tools/blockScroll';

export default {
  name: 'TrainingSectionDocumentItem',
  components: {
    Popup,
    DocumentViewerPopup,
  },
  props: {
    document: {
      type: Object,
      default: null,
    },
    title: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      popupActive: false,
      overlayTop: null,
    };
  },
  computed: {
    overlayStyle() {
      if (this.overlayTop === null) return {};
      return {
        top: `${this.overlayTop}px`,
        transform: 'none',
      };
    },
  },
  mounted() {
    this.positionOverlay();
    window.addEventListener('resize', this.positionOverlay);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.positionOverlay);
  },
  methods: {
    positionOverlay() {
      this.$nextTick(() => {
        const { shape, overlay } = this.$refs;
        if (!shape || !overlay) return;

        const shapeHeight = shape.getBoundingClientRect().height;
        const overlayHeight = overlay.getBoundingClientRect().height;
        this.overlayTop = shapeHeight - overlayHeight - (shapeHeight * 0.07);
      });
    },
    openDocumentPopup() {
      this.popupActive = true;
    },
    closeDocumentPopup() {
      this.popupActive = false;
      unfreeze();
    },
  },
};
</script>

<style lang="scss" scoped>
.training-section-document-item {
  display: block;
  width: 100%;
  height: 100%;
  text-decoration: none;
  color: inherit;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.8;
  }

  .document-card {
    position: relative;
    width: 220px;
    height: 320px;
    background: transparent;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    box-sizing: border-box;

    @include screen("sm", max) {
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      width: 100%;
      height: auto;
      padding: 0;
      gap: 60px;
    }
  }

  .document-shape {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 0;
    pointer-events: none;

    @include wRule(
      width,
      (
        xl: 191,
        lg: 167,
        md: 142,
        sm: 99,
      )
    );

    @include wRule(
      height,
      (
        xl: 280,
        lg: 245,
        md: 209,
        sm: 146,
      )
    );

    @include screen("sm", max) {
      position: relative;
      flex-shrink: 0;
    }
  }

  .document-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    z-index: 20;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 60px 20px 20px 20px;
    box-sizing: border-box;
    pointer-events: none;

    @include screen("sm", max) {
      position: relative;
      padding: 0;
      align-items: flex-start;
      justify-content: center;
      text-align: left;
      flex: none;
      height: auto;
      width: 220px;
      pointer-events: auto;
    }
  }

  .document-title-overlay {
    display: none;

    @include screen("sm", min) {
      display: block;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      padding: 10px 15px;

      @include wRule(
        left,
        (
          xl: 85,
          lg: 75,
          md: 65,
        )
      );

      @include wRule(
        width,
        (
          xl: 280,
          lg: 200,
          md: 180,
        )
      );
      background-color: var(--bg-dark, #14181f);
      font-weight: 400;
      line-height: 1.4;
      color: #FFFFFF;
      text-align: left;
      z-index: 25;
      box-sizing: border-box;

      @include wRule(
        font-size,
        (
          xl: 18,
          lg: 16,
          md: 14,
          sm: 14,
        )
      );
    }
  }

  .document-text {
    font-weight: 400;
    line-height: 1.4;
    color: #FFFFFF;
    text-align: center;
    opacity: 1;
    position: relative;
    z-index: 10;

    @include wRule(
      font-size,
      (
        xl: 20,
        lg: 18,
        md: 16,
        sm: 20,
      )
    );

    @include screen("sm", min) {
      display: none;
    }

    @include screen("sm", max) {
      display: block;
      text-align: left;
    }
  }
}
</style>
